import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { createUser } from '../../services/api/userService';
import { BANK_NAMES } from '../../utils/constants';
import { useTranslation } from '../../hooks/useTranslation';
import './CreateConsumerModal.css';

interface CreateConsumerModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateConsumerModal: React.FC<CreateConsumerModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { userId, userType, bankName: currentUserBankName } = useSelector((state: RootState) => state.auth);
  const currentUserType = userType || '';
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nni: '',
    phoneNumber: '',
    availableBalance: '0.00',
    bankName: currentUserBankName || 'BFI'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Only TWIZE_ADMIN can select bank, others use their own bank
  const showBankSelector = currentUserType === 'TWIZE_ADMIN';

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!userId) {
      setError(t('createConsumer.messages.userIdNotFound'));
      return;
    }

    // Validate NNI format (basic validation)
    if (formData.nni.length < 3) {
      setError(t('createConsumer.messages.nniTooShort'));
      return;
    }

    // Validate phone number format
    if (formData.phoneNumber.length < 8) {
      setError(t('createConsumer.messages.invalidPhone'));
      return;
    }

    // Validate available balance
    const balance = parseFloat(formData.availableBalance);
    if (isNaN(balance) || balance < 0) {
      setError(t('createConsumer.messages.invalidBalance'));
      return;
    }
    
    setLoading(true);

    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      nni: formData.nni,
      phoneNumber: formData.phoneNumber,
      availableBalance: balance,
      userType: 'CONSUMER', // Fixed to CONSUMER
      username: '', // Consumers don't have username
      password: '', // Consumers don't have password
      bankName: formData.bankName,
      createdById: userId // ID of the logged-in user creating this consumer
    };

    console.log('📤 Creating consumer:', userData);

    const result = await createUser(userData, userId);
    setLoading(false);

    if (result.success) {
      onSuccess();
      onClose();
    } else {
      setError(result.message || t('createConsumer.messages.createFailed'));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('createConsumer.title')}</h2>
          <button className="close-button" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="consumer-form">
          {/* Bank Selection */}
          {showBankSelector ? (
            <div className="form-group">
              <label htmlFor="bankName">{t('createConsumer.bankRequired')}</label>
              <select
                id="bankName"
                value={formData.bankName}
                onChange={(e) => handleChange('bankName', e.target.value)}
                required
                disabled={loading}
              >
                {BANK_NAMES.map(bank => (
                  <option key={bank.value} value={bank.value}>{bank.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label>{t('createConsumer.bank')}</label>
              <div className="bank-display">
                {currentUserBankName || t('common.na')}
              </div>
              <small className="form-hint">
                {t('createConsumer.bankHint')}
              </small>
            </div>
          )}

          {/* Personal Information */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('createConsumer.firstNameRequired')}</label>
              <input
                id="firstName"
                type="text"
                placeholder={t('createConsumer.firstNamePlaceholder')}
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t('createConsumer.lastNameRequired')}</label>
              <input
                id="lastName"
                type="text"
                placeholder={t('createConsumer.lastNamePlaceholder')}
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nni">{t('createConsumer.nniRequired')}</label>
              <input
                id="nni"
                type="text"
                placeholder={t('createConsumer.nniPlaceholder')}
                value={formData.nni}
                onChange={(e) => handleChange('nni', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">{t('createConsumer.phoneNumberRequired')}</label>
              <input
                id="phoneNumber"
                type="tel"
                placeholder={t('createConsumer.phoneNumberPlaceholder')}
                value={formData.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Financial Information */}
          <div className="form-group">
            <label htmlFor="availableBalance">{t('createConsumer.availableBalanceRequired')}</label>
            <input
              id="availableBalance"
              type="number"
              step="0.01"
              min="0"
              placeholder={t('createConsumer.availableBalancePlaceholder')}
              value={formData.availableBalance}
              onChange={(e) => handleChange('availableBalance', e.target.value)}
              required
              disabled={loading}
            />
            <small className="form-hint">{t('createConsumer.availableBalanceHint')}</small>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? t('createConsumer.creating') : t('createConsumer.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateConsumerModal;

